import { useCallback, useRef, useState } from 'react';
import { useRobotOperations } from '../../../../api/hooks/binding/robot-operations.binding';
import { useMachineContext } from '../../../../pages/machine/machine-context-provider';
import { logger } from '../../../../util/logger';
import { QNear, Waypoint } from '../../../../util/machine-variable.utils';
import { useFlowServices } from './flow-service.hooks';

export interface MoveToPointProps {
  point?: Waypoint;
  qnear?: QNear;
  tcp?: Waypoint;
  stickout?: number;
  executingIndex?: number;
}

interface MoveToPointResults {
  startMoveToPoint: (props: MoveToPointProps) => Promise<void>;
  isLoading: boolean;
}

const useMoveToPoint = (): MoveToPointResults => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isMovingRef = useRef<boolean>(false);
  const isSendingStartRef = useRef<boolean>(false);

  const { machine } = useMachineContext();
  const operations = useRobotOperations();
  const { flowAnalyticService } = useFlowServices();

  const moveToPoint = useCallback(
    async (
      point: Waypoint,
      qnear: number[],
      stickout: number,
      tcp: Waypoint,
      executingIndex: number
    ): Promise<boolean> => {
      try {
        const result = await operations.moveToPoint(
          machine.id,
          point,
          qnear,
          stickout,
          { tcp, executingIndex }
        );

        if (!result.success) {
          console.error('Move to point operation failed:', result.error);
        }

        return result.success;
      } catch (error) {
        console.error('Error executing move to point operation:', error);
        return false;
      }
    },
    [machine.id, operations]
  );

  const startMoveToPoint = async (props: MoveToPointProps) => {
    const { point, qnear, tcp, stickout, executingIndex } = props;

    setIsLoading(true);

    if (!isMovingRef.current && point && qnear && tcp) {
      isMovingRef.current = true;
      isSendingStartRef.current = true;

      await moveToPoint(point, qnear, stickout ?? 0, tcp, executingIndex ?? 0);

      isMovingRef.current = false;
      isSendingStartRef.current = false;
      logger.info('move to point - started moving');
      flowAnalyticService.trackMoveToPoint();
    }

    setIsLoading(false);
  };

  return { startMoveToPoint, isLoading };
};

export { useMoveToPoint };
